// Captura y persistencia del origen del visitante.
// Guarda el "first touch" (primera vez que el usuario llega con UTMs / clic IDs / referrer)
// en localStorage, de modo que cualquier evento posterior del funnel pueda atribuirse al
// mismo origen aunque el usuario navegue o vuelva sin UTMs en la URL.

const STORAGE_KEY = 'attribution';
const LAST_TOUCH_KEY = 'attribution_last';
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 días

const TRACKED_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
];

function readUrlParams() {
  const out = {};
  try {
    const params = new URLSearchParams(window.location.search);
    TRACKED_PARAMS.forEach((key) => {
      const val = params.get(key);
      if (val) out[key] = val;
    });
  } catch (_) {}
  return out;
}

function readStored(key = STORAGE_KEY) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.captured_at && Date.now() - Number(parsed.captured_at) > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(data, key = STORAGE_KEY) {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // noop
  }
}

// Una visita trae señal de campaña si la URL tiene UTMs o un ID de clic.
// El referrer solo no cuenta: no dice de qué campaña viene.
function hasCampaignSignal(urlParams) {
  return Object.keys(urlParams).length > 0;
}

/**
 * Lee el origen actual y lo persiste en dos capas:
 * - first-touch (`attribution`): la primera vez que llegó, inmutable por 90 días.
 * - last-touch (`attribution_last`): se SOBREESCRIBE cada vez que llega con UTMs o
 *   un ID de clic nuevo.
 *
 * Por qué las dos: antes solo existía el first-touch, así que alguien que ya había
 * visitado el sitio y después hacía clic en un anuncio quedaba registrado como
 * "acceso directo" para siempre. Con campañas pagadas eso subreporta justamente
 * las campañas que estás pagando.
 *
 * Devuelve el objeto consolidado (last-touch si existe, si no el first-touch).
 */
export function captureAttribution() {
  const urlParams = readUrlParams();
  const referrer = (typeof document !== 'undefined' && document.referrer) || '';
  const landingPage = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

  const stored = readStored();
  const hasUrlSignal = hasCampaignSignal(urlParams) || referrer;

  // Señal de campaña nueva: se registra como last-touch, sin tocar el first-touch.
  if (hasCampaignSignal(urlParams)) {
    writeStored(
      {
        ...urlParams,
        referrer: referrer || '',
        landing_page: landingPage,
        captured_at: Date.now(),
      },
      LAST_TOUCH_KEY
    );
  }

  if (!stored && hasUrlSignal) {
    const fresh = {
      ...urlParams,
      referrer: referrer || '',
      landing_page: landingPage,
      captured_at: Date.now(),
    };
    writeStored(fresh);
    return getStoredAttribution();
  }

  if (stored) return getStoredAttribution();

  // Acceso directo sin referrer ni UTMs: guardamos un marcador "directo" para no perder la sesión.
  const direct = {
    referrer: '',
    landing_page: landingPage,
    captured_at: Date.now(),
  };
  writeStored(direct);
  return getStoredAttribution();
}

/** Atribución vigente: el último contacto con campaña si existe, si no el primero. */
export function getStoredAttribution() {
  const last = readStored(LAST_TOUCH_KEY);
  const first = readStored();
  if (last && first) {
    // El last-touch manda en las señales de campaña; del first-touch se conserva
    // la página de entrada original cuando el last-touch no la tenga.
    return { ...first, ...last };
  }
  return last || first || {};
}

/** Solo el primer contacto, sin mezclar. Útil para reportes de origen original. */
export function getFirstTouchAttribution() {
  return readStored() || {};
}

// Payload listo para enviar al backend. Incluye todos los campos aunque vengan vacíos
// para que el backend pueda escribir columnas consistentes en Google Sheets.
export function getAttributionPayload() {
  const data = captureAttribution();
  return {
    utm_source: data.utm_source || '',
    utm_medium: data.utm_medium || '',
    utm_campaign: data.utm_campaign || '',
    utm_content: data.utm_content || '',
    utm_term: data.utm_term || '',
    gclid: data.gclid || '',
    fbclid: data.fbclid || '',
    referrer: data.referrer || '',
    landing_page: data.landing_page || '',
  };
}
